<template>
  <div class="image-region-container flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        <router-link class="d-inline-block text-secondary mr-22-px" :to="'/webpages/' + webpage.id + '#' + ActivatedTabKeys.Image"><a-icon type="arrow-left" /></router-link>
        <span>Image Region Selection</span>
      </span>
      <div class="clearfix">
        <transition-group name="slide-down">
          <template v-if="!isLoadingImage">
            <a-button key="reset" size="default" html-type="button" type="danger" class="a-header--button ml-10-px" :disabled="isResetImage || isLoadingImage || isCompleting" @click="resetImage">
              <RebaseIcon :spin="isResetImage" />
              <span class="ml-2">Reset</span>
            </a-button>
            <a-button key="refresh" size="default" html-type="button" class="a-header--button btn-bg-secondary" :disabled="isRefreshImage || isLoadingImage || isCompleting" @click="refreshImage">
              <a-icon type="sync" :spin="isRefreshImage" />
              <span>Refresh</span>
            </a-button>
          </template>
        </transition-group>
      </div>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoadingImage">
      <Spinner />
    </div>
    <div class="a-body flex-grow-1 p-20-px">
      <transition name="slide-up">
        <template v-if="!isLoadingImage">
          <div>
            <p class="d-block p-10-px text-left radius-4-px bg-description notice-text">
              Select and double click the region you wish to monitor
            </p>
            <div class="d-flex justify-content-center">
              <div ref="imageSelectionArea" class="image-selection-area">
                <div class="selectable" @mousedown="onMouseDownSelectable" @mouseup="onMouseUpSelectable" @mousemove="onMouseMoveSelectable"></div>
              </div>
            </div>
          </div>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <a-tooltip title="Back to edit page">
        <a-button type="primary" size="large" html-type="button" class="slide-down-enter-active" :loading="isCompleting" :disabled="isLoadingImage">
          <router-link :to="'/webpages/' + webpage.id + '#' + ActivatedTabKeys.Image">Complete</router-link>
        </a-button>
      </a-tooltip>
    </div>
  </div>
</template>

<script>
  import RebaseIcon from '@shared/svg/RebaseIcon';
  import { WEBPAGE_WARNING_LIMIT_MIN } from '@constants/webpageWarningLimit';
  import { isCanvasSupported } from '@shared/helpers/utils';
  import WebpageService from '../services/webpage.service';
  import { ActivatedTabKeys } from '../consts/Webpage.consts';

  export default {
    name: 'ImageRegion',
    components: {
      RebaseIcon
    },
    data() {
      return {
        ActivatedTabKeys,
        isCanvasSupported,
        isRefreshImage: false,
        isResetImage: false,
        isCompleting: false,
        isLoadingImage: true,
        isBrowserSupportBroadCast: false,
        imageRegionBroadcastChannel: null,
        webpage: {
          id: ''
        },
        webpageScreenShot: '',
        webpageScreenShotSize: {
          width: 0,
          height: 0
        },
        imageRegions: [],
        getWebpageRequest$: null,
        isMousePressedToSelectArea: false,
        selectedAreaElement: null,
        isMousePressedToMoveArea: false,
        startSelectMousePosition: {
          x: 0,
          y: 0
        },
        endSelectMousePosition: {
          x: 0,
          y: 0
        },
        startMoveAreaMousePosition: {
          x: 0,
          y: 0
        },
        startEndAreaMousePosition: {
          x: 0,
          y: 0
        }
      };
    },
    beforeCreate() {
      this.isBrowserSupportBroadCast = 'BroadcastChannel' in window;
    },
    beforeMount() {
      this.getWebpageRequest$ = this.$cancelRequest();
    },
    mounted() {
      if (this.isBrowserSupportBroadCast) {
        this.imageRegionBroadcastChannel = new BroadcastChannel('image-region-channel');
        this.imageRegionBroadcastChannel.onmessage = () => {
          this.refreshImage();
        };
      }
      this.fetchWebpage().then(() => {
        this.drawImage();
      });
    },
    beforeDestroy() {
      this.imageRegionBroadcastChannel && this.imageRegionBroadcastChannel.close();
      this.getWebpageRequest$.cancel('Redirect to another page');
    },
    methods: {
      async fetchWebpage() {
        const webpageId = this.$route.params && this.$route.params.id;
        if (!webpageId) {
          this.$showErrorMessage('Webpage id not exist!');
          this.$router.replace('/webpages/list');
          return;
        }
        try {
          const [webpageData, dataScreenshot] = await Promise.all([WebpageService.get(+webpageId, this.getWebpageRequest$.token), WebpageService.getImagesAnalytics(+webpageId)]);
          this.webpage = { ...webpageData };
          if (this.$route.query && this.$route.query.threshold && !isNaN(+this.$route.query.threshold)) {
            this.webpage.threshold.image_similarity = +this.$route.query.threshold;
          }
          this.webpageScreenShot = dataScreenshot.screenshot;
          this.imageRegions = webpageData.image_regions || [];
          this.isLoadingImage = false;
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isLoadingImage = false;
        }
      },
      formatBody(webpage, imageRegions) {
        const threshold = { ...webpage.threshold };
        delete threshold.id;
        delete threshold.url_id;
        const body = {
          config_name: webpage.config_name,
          plan_type: webpage.plan_type,
          polling_interval: webpage.polling_interval,
          continuous_monitoring: !!webpage.continuous_monitoring,
          warning_limit: Math.max(webpage.warning_limit, WEBPAGE_WARNING_LIMIT_MIN),
          threshold: { ...threshold },
          monitoring_configuration: {
            user_agent_id: webpage.monitoring_configuration.user_agent_id,
            text_string_appear: webpage.monitoring_configuration.text_string_appear,
            text_string_not_appear: webpage.monitoring_configuration.text_string_not_appear
          },
          current_baseline: {
            title: webpage.current_baseline.title,
            line_num: webpage.current_baseline.line_num,
            count_href: webpage.current_baseline.count_href,
            count_script: webpage.current_baseline.count_script,
            count_link: webpage.current_baseline.count_link,
            count_image: webpage.current_baseline.count_image,
            count_div: webpage.current_baseline.count_div,
            count_iframe: webpage.current_baseline.count_iframe,
            count_form: webpage.current_baseline.count_form,
            hash: webpage.current_baseline.hash,
            baseline_hrefs: (webpage.current_baseline.baseline_hrefs || []).map((item) => ({
              id: item.id,
              selected: item.selected
            })),
            baseline_iframes: (webpage.current_baseline.baseline_iframes || []).map((item) => ({
              id: item.id,
              selected: item.selected
            })),
            baseline_forms: (webpage.current_baseline.baseline_forms || []).map((item) => ({
              id: item.id,
              selected: item.selected
            })),
            baseline_images: (webpage.current_baseline.baseline_images || []).map((item) => ({
              id: item.id,
              selected: item.selected
            })),
            baseline_scripts: (webpage.current_baseline.baseline_scripts || []).map((item) => ({
              id: item.id,
              selected: item.selected
            })),
            baseline_stylesheets: (webpage.current_baseline.baseline_stylesheets || []).map((item) => ({
              id: item.id,
              selected: item.selected
            }))
          },
          image_regions: [...imageRegions].map((item) => ({
            coord_x: item.coord_x,
            coord_y: item.coord_y,
            coord_w: item.coord_w,
            coord_h: item.coord_h
          }))
        };
        return body;
      },
      async refreshImage() {
        this.isRefreshImage = true;
        const webpageId = this.$route.params && this.$route.params.id;
        try {
          const [webpageData, dataScreenshot] = await Promise.all([WebpageService.get(+webpageId, this.getWebpageRequest$.token), WebpageService.getImagesAnalytics(+webpageId)]);
          this.webpage = { ...webpageData };
          this.webpageScreenShot = dataScreenshot.screenshot;
          this.imageRegions = webpageData.image_regions || [];
          this.drawImage();
        } catch (error) {
          this.$showErrorMessage('HTTP error received from web server.!');
        } finally {
          setTimeout(() => {
            this.isRefreshImage = false;
          }, 300);
        }
      },
      async resetImage() {
        this.isResetImage = true;
        try {
          this.imageRegions.length = 0;
          const body = this.formatBody(this.webpage, []);
          const webpageData = await WebpageService.updateUrlConfig(this.webpage.id, body);
          if (this.selectedAreaElement) {
            this.selectedAreaElement.remove();
          }
          this.webpage = { ...webpageData };
          this.imageRegions = webpageData.image_regions || [];
          this.drawImage();
          this.$showSuccessMessage('Updated image regions.');
          this.imageRegionBroadcastChannel && this.imageRegionBroadcastChannel.postMessage('Should update image resigons.');
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isResetImage = false;
        }
      },
      async saveImageRegions() {
        this.isCompleting = true;
        try {
          const { top, left, height, width } = this.selectedAreaElement.style;
          const newRegion = {
            coord_h: +height.replace('px', ''),
            coord_w: +width.replace('px', ''),
            coord_x: +left.replace('px', ''),
            coord_y: +top.replace('px', '')
          };
          const body = this.formatBody(this.webpage, [...this.imageRegions, newRegion]);
          const webpageData = await WebpageService.updateUrlConfig(this.webpage.id, body);
          if (this.selectedAreaElement) {
            this.selectedAreaElement.remove();
          }
          this.webpage = { ...webpageData };
          this.imageRegions = webpageData.image_regions || [];
          this.drawImage();
          this.$showSuccessMessage('Updated image regions.');
          this.imageRegionBroadcastChannel && this.imageRegionBroadcastChannel.postMessage('Should update image resigons.');
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isCompleting = false;
        }
      },
      drawImage() {
        const imgElement = new Image();
        imgElement.src = this.webpageScreenShot;
        imgElement.alt = 'Image analytics';
        setTimeout(() => {
          if (this.isCanvasSupported) {
            let canvasElement = this.$refs.imageSelectionArea.querySelector('CANVAS');
            if (!canvasElement) {
              canvasElement = document.createElement('CANVAS');
            }
            const ctx = canvasElement.getContext('2d');
            ctx.canvas.width = imgElement.width;
            ctx.canvas.height = imgElement.height;
            ctx.drawImage(imgElement, 0, 0);
            for (const iterator of this.imageRegions) {
              ctx.beginPath();
              ctx.fillStyle = 'rgba(255, 62, 62, 0.2)';
              ctx.fillRect(iterator.coord_x, iterator.coord_y, iterator.coord_w, iterator.coord_h);
              ctx.stroke();
              ctx.beginPath();
            }
            this.$refs.imageSelectionArea.appendChild(canvasElement);
          } else {
            this.$refs.imageSelectionArea.appendChild(imgElement);
            for (const [index, iterator] of this.imageRegions.entries()) {
              const selectedAreaElement = document.createElement('DIV');
              selectedAreaElement.style.position = 'absolute';
              selectedAreaElement.style.zIndex = `${index + 3}`;
              selectedAreaElement.style.backgroundColor = 'rgba(255, 62, 62, 0.2)';
              selectedAreaElement.style.top = `${iterator.coord_y}px`;
              selectedAreaElement.style.left = `${iterator.coord_x}px`;
              selectedAreaElement.style.width = `${iterator.coord_w}px`;
              selectedAreaElement.style.height = `${iterator.coord_h}px`;
              this.$refs.imageSelectionArea.appendChild(selectedAreaElement);
            }
          }
          this.webpageScreenShotSize.width = imgElement.width;
          this.webpageScreenShotSize.height = imgElement.height;
        });
      },
      onMouseDownSelectable(mouseEvent) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        this.isMousePressedToSelectArea = true;
        this.startSelectMousePosition = this.getMousePosition(mouseEvent);
        if (this.selectedAreaElement) {
          this.selectedAreaElement.remove();
        }
      },
      onMouseUpSelectable(mouseEvent) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        this.isMousePressedToSelectArea = false;
        setTimeout(() => {
          if (this.selectedAreaElement.style.top === 'auto') {
            const bottomValue = +this.selectedAreaElement.style.bottom.replace('px', '');
            const heightValue = +this.selectedAreaElement.style.height.replace('px', '');
            this.selectedAreaElement.style.top = `${Math.max(this.webpageScreenShotSize.height - bottomValue - heightValue + 6, 0)}px`;
            this.selectedAreaElement.style.bottom = 'auto';
          }
          if (this.selectedAreaElement.style.left === 'auto') {
            const rightValue = +this.selectedAreaElement.style.right.replace('px', '');
            const widthValue = +this.selectedAreaElement.style.width.replace('px', '');
            this.selectedAreaElement.style.left = `${Math.max(this.webpageScreenShotSize.width - rightValue - widthValue, 0)}px`;
            this.selectedAreaElement.style.right = 'auto';
          }
        });
      },
      onMouseMoveSelectable(mouseEvent) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        if (this.isMousePressedToSelectArea) {
          const { x: mouseX, y: mouseY } = this.getMousePosition(mouseEvent);
          this.selectedAreaElement = this.$refs.imageSelectionArea.querySelector('#selected-area-element');
          if (!this.selectedAreaElement) {
            this.selectedAreaElement = document.createElement('div');
            this.selectedAreaElement.setAttribute('id', 'selected-area-element');
            this.selectedAreaElement.setAttribute('draggable', true);
            this.selectedAreaElement.style.cursor = 'move';
            this.selectedAreaElement.style.position = 'absolute';
            this.selectedAreaElement.style.zIndex = 2;
            this.selectedAreaElement.style.backgroundColor = '#acacaca3';
            this.selectedAreaElement.addEventListener('mousedown', (selectedAreaMouseEvent) => {
              this.isMousePressedToMoveArea = true;
              this.startMoveAreaMousePosition = this.getMousePosition(selectedAreaMouseEvent);
            });
            this.selectedAreaElement.addEventListener('mouseup', () => {
              this.isMousePressedToMoveArea = false;
            });
            this.selectedAreaElement.addEventListener('mousemove', this.onMouseMoveSelectedArea);
            this.selectedAreaElement.addEventListener('dblclick', () => {
              this.$confirm({
                title: 'Do you wish to monitor this region?',
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  this.saveImageRegions();
                }
              });
            });
            this.$refs.imageSelectionArea.prepend(this.selectedAreaElement);
          }
          const isMouseMoveDown = mouseY > this.startSelectMousePosition.y;
          const isMouseMoveRight = mouseX > this.startSelectMousePosition.x;
          if (isMouseMoveDown) {
            this.selectedAreaElement.style.top = `${Math.ceil(this.startSelectMousePosition.y)}px`;
            this.selectedAreaElement.style.bottom = 'auto';
            this.selectedAreaElement.style.height = `${Math.abs(Math.ceil(mouseY) - Math.ceil(this.startSelectMousePosition.y) - 5)}px`;
          } else {
            this.selectedAreaElement.style.bottom = `${Math.ceil(this.webpageScreenShotSize.height) - Math.ceil(this.startSelectMousePosition.y)}px`;
            this.selectedAreaElement.style.top = 'auto';
            this.selectedAreaElement.style.height = `${Math.abs(Math.ceil(mouseY) - Math.ceil(this.startSelectMousePosition.y))}px`;
          }
          if (isMouseMoveRight) {
            this.selectedAreaElement.style.left = `${Math.ceil(this.startSelectMousePosition.x)}px`;
            this.selectedAreaElement.style.right = 'auto';
            this.selectedAreaElement.style.width = `${Math.abs(Math.ceil(mouseX) - Math.ceil(this.startSelectMousePosition.x) - 5)}px`;
          } else {
            this.selectedAreaElement.style.right = `${Math.ceil(this.webpageScreenShotSize.width) - Math.ceil(this.startSelectMousePosition.x)}px`;
            this.selectedAreaElement.style.left = 'auto';
            this.selectedAreaElement.style.width = `${Math.abs(Math.ceil(mouseX) - Math.ceil(this.startSelectMousePosition.x) + 5)}px`;
          }
        }
      },
      onMouseMoveSelectedArea(mouseEvent) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        if (this.isMousePressedToMoveArea) {
          const widthOfSelectedArea = +this.selectedAreaElement.style.width.replace('px', '');
          const heightOfSelectedArea = +this.selectedAreaElement.style.height.replace('px', '');
          const { x: mouseX, y: mouseY } = this.getMousePosition(mouseEvent);
          const addLeftValue = mouseX - this.startMoveAreaMousePosition.x - 5;
          const addTopValue = mouseY - this.startMoveAreaMousePosition.y - 5;
          const leftValue = Math.max(+this.selectedAreaElement.style.left.replace('px', '') + addLeftValue, 0);
          const topValue = Math.max(+this.selectedAreaElement.style.top.replace('px', '') + addTopValue, 0);
          if (leftValue + widthOfSelectedArea <= this.webpageScreenShotSize.width) {
            this.selectedAreaElement.style.left = `${leftValue}px`;
          }
          if (topValue + heightOfSelectedArea <= this.webpageScreenShotSize.height) {
            this.selectedAreaElement.style.top = `${topValue}px`;
          }
        }
      },
      getMousePosition(mouseEvent) {
        const rect = mouseEvent.target.getBoundingClientRect();
        const x = mouseEvent.clientX - rect.left;
        const y = mouseEvent.clientY - rect.top;
        return { x, y };
      }
    }
  };
</script>

<style lang="scss" scoped>
  .image-selection-area {
    width: max-content;
    position: relative;
    .selectable:hover {
      background-color: #79797952;
    }
    div#selected-area-element + .selectable:hover {
      background-color: transparent;
    }
    .selectable:active,
    .selectable:active:hover,
    .selectable:active:focus {
      background-color: transparent;
    }
    div.selectable {
      position: absolute;
      z-index: 1;
      width: 100%;
      height: 100%;
      cursor: crosshair;
      background-color: transparent;
    }
  }
</style>
